/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import config from 'js-sdk/src/jrs.configs';
import formatsMappingUtil from './formatsMappingUtil';
import { Formatter } from './types';

function formatBigValues(
    numberValue: string,
    fixedMin: number,
    fixedMax: number,
    decimalSeparator?: string,
    thousandsSeparator?: string,
    groupingSize?: number
): string {
    const decimalParts = (typeof numberValue !== 'string' ? (+numberValue).toString() : numberValue).split('.');
    const setGroupingSize = groupingSize || 3;
    let intPart = decimalParts[0];
    let mantissPart = decimalParts[1] || '';
    const mantissPartValue = decimalParts[1] ? +(`0.${mantissPart}`) : 0;
    let fixed = mantissPart.length;
    let integerParts;
    fixed = Math.max(fixed, fixedMin);
    fixed = Math.min(fixed, fixedMax);
    if (fixed) {
        if (mantissPart.length > fixed) {
            mantissPart = mantissPartValue.toFixed(fixed).substring(2);
            if (+mantissPart === 0 && Math.round(mantissPartValue)) {
                intPart = (+intPart + 1).toString();
            }
        } else if (mantissPart.length < fixed) {
            for (let i = mantissPart.length; i < fixed; i += 1) {
                mantissPart += '0';
            }
        }
    } else if (intPart.length < 17 && Math.round(mantissPartValue)) {
        intPart = (+intPart + 1).toString();
    }
    if (thousandsSeparator) {
        integerParts = [];
        let i;
        for (i = intPart.length - setGroupingSize; i >= 0; i -= setGroupingSize) {
            integerParts.push(intPart.substr(i, setGroupingSize));
        }
        if (setGroupingSize + i > 0) {
            integerParts.push(intPart.substr(0, setGroupingSize + i));
        }
        integerParts.reverse();
        intPart = integerParts.join(thousandsSeparator);
    }
    return fixed ? intPart + (decimalSeparator || '.') + mantissPart : intPart;
}

interface INumberFormatOptions {
    decimalSeparator?: string,
    thousandsSeparator?: string,
    fixedMin?: number,
    fixedMax?: number,
    integerMin?: number,
    groupingSize?: number,
    prefix?: string,
    suffix?: string,
    negativePrefix?: string,
    negativeSuffix?: string,
}

const optionsCache: { [locale: string] : {
    [format: string]: INumberFormatOptions
}} = {}

function formatNumber(
    numberValue: string,
    formatOptions: INumberFormatOptions
): string {

    const {
        decimalSeparator, thousandsSeparator, fixedMin, fixedMax, groupingSize, prefix, suffix, negativePrefix, negativeSuffix
    } = formatOptions;

    let numberAsString = `${numberValue}`;
    const negative = numberAsString[0] === '-';
    if (negative) {
        numberAsString = numberValue.substring(1);
    }

    let res = formatBigValues(numberAsString, fixedMin || 0, fixedMax || 0, decimalSeparator, thousandsSeparator, groupingSize);

    if (negative) {
        if (negativePrefix) {
            res = negativePrefix + res;
        } else if (prefix) {
            res = `-${prefix}${res}`;
        } else {
            res = `-${res}`;
        }
        if (negativeSuffix) {
            res += negativeSuffix;
        } else if (suffix) {
            res += suffix;
        }
    } else {
        if (prefix) {
            res = prefix + res;
        }
        if (suffix) {
            res += suffix;
        }
    }
    return res;
}

type SEPARATOR_POSITION = 'THOUSAND' | 'DECIMAL';
type SEPARATOR = { [type in SEPARATOR_POSITION]?: string };

function separatorsFactory(locale: string): SEPARATOR {
    const separators: { [_: string]: SEPARATOR } = {
        en: {
            THOUSAND: ','
        },
        en_US: {
            THOUSAND: ','
        },
        es: {
            DECIMAL: ',',
            THOUSAND: '.'
        },
        de: {
            DECIMAL: ',',
            THOUSAND: '.'
        },
        it: {
            DECIMAL: ',',
            THOUSAND: '.'
        },
        pt_BR: {
            DECIMAL: ',',
            THOUSAND: '.'
        },
        fr: {
            DECIMAL: ',',
            THOUSAND: ' '
        },
        ja: {
            THOUSAND: ','
        },
        zh_CN: {
            THOUSAND: ','
        }
    };
    let formatter = separators[locale];
    if (formatter === undefined) {
        const [lang] = locale.split('_');

        formatter = separators[lang] || {
            DECIMAL: '.',
            THOUSAND: ','
        }
    }
    return formatter;
}

function parsePart(part: string = ''): {suffix: string, number: string, prefix: string} {
    const suffix: string[] = [],
        number : string[] = [],
        prefix: string[] = [];

    let current:string[] = prefix;
    let inNumber = false;

    for (let i = 0; i < part.length; i += 1) {
        if (part[i] === '\'') {
            if (!inNumber && i + 1 < part.length) {
                i += 1;
            } else {
                throw new Error('Illeagal position of an escape charater in pattern');
            }
        } else if (part[i] === '0' || part[i] === '#' || part[i] === '.' || part[i] === ',') {
            inNumber = true;
            current = number;
        } else if (inNumber) {
            inNumber = false;
            current = suffix;
        }

        current.push(part[i]);
    }

    return {
        prefix: prefix.join(''),
        number: number.join(''),
        suffix: suffix.join('')
    };
}

function parseOptions(format: string, separator: SEPARATOR): INumberFormatOptions {
    const [positivePart, negativePart] = format.split(';');
    const { prefix, number, suffix } = parsePart(positivePart);
    const { prefix: negativePrefix, suffix: negativeSuffix } = parsePart(negativePart);
    const [integralPart, decimalPart] = number.split('.');

    const res: INumberFormatOptions = {
        prefix, suffix, negativePrefix, negativeSuffix
    };

    if (decimalPart) {
        res.decimalSeparator = separator.DECIMAL;
        res.fixedMax = decimalPart.length;
        res.fixedMin = decimalPart.split('#').join('').length
    }

    if (integralPart) {
        const groupingParts = integralPart.split(',');
        if (groupingParts.length > 1) {
            res.thousandsSeparator = separator.THOUSAND;
            res.groupingSize = groupingParts[groupingParts.length - 1].length;
        }
    }

    return res;

}

function optionsFactory(format: string): INumberFormatOptions {
    if (!optionsCache[config.userLocale]) {
        const separator = separatorsFactory(config.userLocale);

        optionsCache[config.userLocale] = {
            '#,##0.00': {
                decimalSeparator: separator.DECIMAL,
                thousandsSeparator: separator.THOUSAND,
                fixedMin: 2,
                fixedMax: 2
            },
            0: {
                thousandsSeparator: '',
                fixedMin: 0,
                fixedMax: 0
            },
            '$#,##0.00;($#,##0.00)': {
                decimalSeparator: separator.DECIMAL,
                thousandsSeparator: separator.THOUSAND,
                fixedMin: 2,
                fixedMax: 2,
                prefix: '$',
                negativePrefix: '($',
                negativeSuffix: ')'
            },
            '$#,##0;($#,##0)': {
                thousandsSeparator: separator.THOUSAND,
                fixedMin: 0,
                fixedMax: 0,
                prefix: '$',
                negativePrefix: '($',
                negativeSuffix: ')'
            },
            '$#,##0.000#############': {
                decimalSeparator: separator.DECIMAL,
                thousandsSeparator: separator.THOUSAND,
                fixedMin: 3,
                fixedMax: 16,
                prefix: '$'
            },
            '$#,##0,00;($#,##0,00)': {
                decimalSeparator: separator.DECIMAL,
                thousandsSeparator: separator.THOUSAND,
                fixedMin: 2,
                fixedMax: 2,
                prefix: '$',
                negativePrefix: '($',
                negativeSuffix: ')'
            },
            '#,##0': {
                thousandsSeparator: separator.THOUSAND,
                fixedMin: 0,
                fixedMax: 0
            },
            '#,##0;(#,##0)': {
                thousandsSeparator: separator.THOUSAND,
                fixedMin: 0,
                fixedMax: 0,
                negativePrefix: '(',
                negativeSuffix: ')'
            }
        }
    }

    if (!optionsCache[config.userLocale][format]) {
        optionsCache[config.userLocale][format] = parseOptions(format, separatorsFactory(config.userLocale));
    }

    return optionsCache[config.userLocale][format];
}

const polyfillFormatter: Formatter = {
    format(val: string, format?: string): string {
        if (formatsMappingUtil.isEmpty(val)) {
            return '';
        }
        if (formatsMappingUtil.isOtherNode(val)) {
            return val;
        }
        if (!format) {
            return val;
        }
        const formatOptions = optionsFactory(format);
        if (formatOptions) {
            return formatNumber(val, formatOptions);
        }
        return val;
    }
};

export default polyfillFormatter;
